/* eslint-disable no-undef */
// Modules
import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTracking } from 'react-tracking'
import { useCustomLayoutStore, usePhasedLoginStore } from 'redesign/store'
import { shallow } from 'zustand/shallow'
// Application
import { clearCpf, getQueryParamsValue, removeQueryParam } from 'utils'
import { analytics } from 'redesign/constants/analytics'
import { PhasedLoginFFType } from 'redesign/typings/featureFlags'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useActiveCIPOrphanHook } from 'hooks/useActiveCIPOrphanHook'
import { LoginsTypes } from 'constants/analytics'
import { useRedirectTo, useRefreshToken } from 'hooks'
import { goToPage } from 'redesign/utils/goToPage'
import { SESSION_EXPIRED_KEY, KEEP_LOGGED_LS_KEY } from 'constants/index'
import { getkeepLoggedLs, getUserToken, setUserToken, setkeepLoggedLs } from 'modules/ApolloClient/utils'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any

const steps = {
    PHASED_LOGIN_INPUT_DOCUMENT: 1,
    PHASED_LOGIN_ONBOARDING_NEW_USER: 2,
}

export const usePhasedLogin = () => {
    const [loading, setLoading] = useState(true)
    const location = useLocation()
    const { trackEvent } = useTracking()
    const { getConfigByName } = useRemoteConfig()
    const { viewerSearchPatient } = useActiveCIPOrphanHook()
    const { layoutConfig } = useCustomLayoutStore()
    const { redirectTo } = useRedirectTo()
    const { refreshToken } = useRefreshToken()
    const { visibility: phasedLoginVisibility, onboarding_page: onboardingPage }: PhasedLoginFFType =
        ((getConfigByName('NAV_PHASED_LOGIN') as unknown) as PhasedLoginFFType) || defaultConfig.jsons.NAV_PHASED_LOGIN
    const {
        step,
        setStep,
        nextStepOnboardingPage,
        setNextStepOnboardingPage,
        laboratoryName,
        setLaboratoryName,
    } = usePhasedLoginStore(
        state => ({
            step: state.step,
            setStep: state.setStep,
            nextStepOnboardingPage: state.nextStepOnboardingPage,
            setNextStepOnboardingPage: state.setNextStepOnboardingPage,
            laboratoryName: state.laboratoryName,
            setLaboratoryName: state.setLaboratoryName,
        }),
        shallow,
    )

    const nextStep = useCallback(() => setStep(step + 1), [setStep, step])
    const previousStep = () => setStep(step - 1)
    const laboratoryParam = useMemo(() => getQueryParamsValue(location, 'laboratorio'), [location])

    const setUrlNextOnboardingStep = (url: string) => setNextStepOnboardingPage(url)

    const getStep = useCallback(() => {
        if ((laboratoryParam || layoutConfig) && step < 1) {
            nextStep()
            return 1
        }

        return step
    }, [laboratoryParam, layoutConfig, step, nextStep])

    const dispatchAnalytics = useCallback(
        (
            event:
                | 'select_platform'
                | 'insert_cpf'
                | 'insert_password'
                | 'insert_cip'
                | 'warning_mode'
                | 'login_failure'
                | 'login_failure_cpf',
        ) => {
            // eslint-disable-next-line prefer-destructuring
            const eventSteps = analytics.phasedLogin.funnel_step
            trackEvent(eventSteps[event])
        },
        [trackEvent],
    )

    const dispatchAnalyticsABTest = useCallback((layout: 'new_login' | 'default' | 'brand_emphasis', brand: string) => {
        window.navFeatureFlagsToAnalytics = window.navFeatureFlagsToAnalytics || {}
        window.navFeatureFlagsToAnalytics.loginABTest = {
            layout,
            brand,
        }
    }, [])

    const redirectIfLogged = () => {
        const redirectPathLogin = redirectTo
        const {
            strings: { AUTH_REDIRECT_LOGIN },
        } = defaultConfig
        const redirectLink = redirectPathLogin ? redirectPathLogin : AUTH_REDIRECT_LOGIN
        goToPage(redirectLink)
    }

    const loginSuccessEvent = async (patientId: number, username: string, keepLogged = false) => {
        const cpfWithNumbersOnly = clearCpf(username)
        trackEvent({
            event: LoginsTypes.EVENTS.LOGIN_SUCCESS,
            funnel: {
                name: LoginsTypes.FUNNELS.LOGIN_FASEADO,
                current_step: 'Login CPF',
                previous_step: undefined,
            },
            page: {
                path: document.location.pathname,
                title: document.title,
            },
            user: {
                id: patientId,
                cpf: cpfWithNumbersOnly,
                access: keepLogged ? 'Automático' : 'CPF',
                profile: 'Completo',
            },
            login_options: {
                remember_me: keepLogged,
            },
        })
    }

    const checkKeepLoggedData = async () => {
        setLoading(true)

        const urlSessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY)

        const redirectToPath = getQueryParamsValue(location, 'redirectTo')
        const redirectToParams = removeQueryParam(location, 'redirectTo')

        const urlRedirectTo = `${redirectToPath || ''}${redirectToParams || ''}`

        const keepLoggedLs = await getkeepLoggedLs()
        const LiviaLS = await getUserToken()

        try {
            const username: string = keepLoggedLs?.keepLogged?.username ?? sessionStorage.getItem('cpf')

            const { id } = await viewerSearchPatient({
                cpf: username,
            })

            const patientId = keepLoggedLs?.keepLogged?.patientId ?? id

            if (!keepLoggedLs?.token && !LiviaLS?.token) {
                return
            }

            if (!keepLoggedLs?.token && LiviaLS?.token) {
                loginSuccessEvent(patientId, username)
                redirectIfLogged()
                return
            }

            const data = await refreshToken(keepLoggedLs)
            const { access_token: newAccessToken, refresh_token: newRefreshToken } = data?.refreshToken || {}

            const updatedTokenLs = {
                ...keepLoggedLs,
                token: newAccessToken,
                refreshToken: newRefreshToken,
                keepLogged: {
                    ...keepLoggedLs.keepLogged,
                    username,
                },
            }

            setUserToken(updatedTokenLs)
            setkeepLoggedLs(updatedTokenLs)

            loginSuccessEvent(patientId, username, true)

            const redirectLink = urlSessionExpired || urlRedirectTo || `/plataforma/inicio${location.search}`

            window.history.pushState({}, '', redirectLink)
        } catch {
            localStorage.removeItem(KEEP_LOGGED_LS_KEY)
        } finally {
            setLoading(false)
        }
    }

    return {
        step,
        steps,
        onboardingPage,
        nextStepOnboardingPage,
        laboratoryName,
        phasedLoginVisibility,
        loading,
        getStep,
        nextStep,
        setUrlNextOnboardingStep,
        setLaboratoryName,
        previousStep,
        dispatchAnalytics,
        dispatchAnalyticsABTest,
        viewerSearchPatient,
        loginSuccessEvent,
        redirectIfLogged,
        checkKeepLoggedData,
    }
}
